// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/DoorCodeReveal.tsx
// 🔧 PURPOSE: Il codice della porta sulla Home, in due tempi come in MyStay:
//             1. il riquadro crema "Per sicurezza inserisci l'email della
//                prenotazione", il campo e "Verifica e mostra il codice";
//             2. il pannello espresso "CODICE PORTA · APP. 101" con il codice
//                in grande, "Copia il codice" e NASCONDI. Per un gruppo, un
//                codice per appartamento.
//
//             La logica è quella di MyStay (useDoorCode): l'email si scrive
//             ogni volta, il codice resta solo in memoria e si dimentica con
//             NASCONDI, uscendo o cambiando prenotazione. Chi usa questo
//             pezzo gli passa il risultato di useDoorCode (così anche la
//             tessera "Codice porta" sa se il codice è visibile).
// ============================================

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Loader2, ShieldCheck } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { UseDoorCodeReturn } from '../../hooks/useDoorCode';
import { FOCUS_RING, FOCUS_RING_ON_DARK, InkButton, INK_TEXT } from './ui';

export interface DoorCodeRevealProps {
  /** Il risultato di useDoorCode(token, booking). */
  door: UseDoorCodeReturn;
  /** Il nome dell'appartamento per l'intestazione del codice (prenotazione singola). */
  apartmentName?: string | null;
  /** Una nota sotto la richiesta dell'email (per esempio la tassa ancora da pagare). */
  note?: React.ReactNode;
  /** Il campo email prende il focus appena compare (aperto da un tocco). */
  autoFocus?: boolean;
  /** Per gli id del campo: uno diverso se ce ne sono due nella pagina. */
  idPrefix?: string;
  className?: string;
}

const DoorCodeReveal: React.FC<DoorCodeRevealProps> = ({
  door,
  apartmentName,
  note,
  autoFocus = true,
  idPrefix = 'home-door',
  className,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const emailId = `${idPrefix}-email`;
  const errorId = `${idPrefix}-error`;

  useEffect(() => {
    if (!autoFocus || door.revealed) return;
    try {
      inputRef.current?.focus({ preventScroll: true });
    } catch {
      inputRef.current?.focus();
    }
  }, [autoFocus, door.revealed]);

  if (door.revealed) {
    const codes = door.apartments && door.apartments.length > 0
      ? door.apartments.map((apt, index) => ({ key: `${index}-${apt.apartmentName}`, name: apt.apartmentName, code: apt.accessCode }))
      : [{ key: 'single', name: door.data?.apartmentName || apartmentName || '', code: door.code }];
    return (
      <div className={cn('flex flex-col gap-2 rounded-[14px] bg-nva-espresso px-3.5 py-3 dark:bg-stone-950', className)}>
        <ul className="flex flex-col gap-2.5">
          {codes.map((item) => (
            <li key={item.key} className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-nva-gold">
                  {item.name ? t('home.quick.doorHeader', { apartment: item.name }) : t('home.quick.door')}
                </span>
                <span className="font-display text-[26px] font-bold leading-tight tracking-[0.14em] text-nva-bone">
                  {item.code || '—'}
                </span>
              </div>
              {item.code ? (
                <button
                  type="button"
                  onClick={() => void door.copy(item.code as string)}
                  aria-label={t('home.quick.doorCopy')}
                  className={cn(
                    'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[10px] border border-nva-bone/30 text-nva-bone',
                    FOCUS_RING_ON_DARK,
                  )}
                >
                  {door.copiedCode === item.code
                    ? <Check className="h-[18px] w-[18px] text-nva-gold" aria-hidden="true" />
                    : <Copy className="h-[18px] w-[18px]" aria-hidden="true" />}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        <span className="sr-only" aria-live="polite">{door.copiedCode ? t('common.copied') : ''}</span>
        <button
          type="button"
          onClick={door.hide}
          className={cn(
            'min-h-[44px] self-end rounded-[10px] border border-nva-bone/30 px-3 text-[12px] font-extrabold uppercase tracking-[0.1em] text-nva-bone',
            FOCUS_RING_ON_DARK,
          )}
        >
          {t('home.quick.doorHide')}
        </button>
      </div>
    );
  }

  const message = door.emailError || door.error;
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void door.verify();
      }}
      className={cn(
        'flex flex-col gap-2.5 rounded-l-[4px] rounded-r-[14px] border-l-[3px] border-nva-gold bg-nva-cream p-3 dark:bg-nva-cream-dark',
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <ShieldCheck className="mt-px h-[18px] w-[18px] flex-shrink-0 text-nva-gold-text dark:text-nva-gold-dark" aria-hidden="true" />
        <p className={cn('m-0 text-[13.5px] leading-[1.35]', INK_TEXT)}>{t('home.quick.doorPrompt')}</p>
      </div>
      {note ? <p className="m-0 text-[12.5px] leading-snug text-nva-muted dark:text-stone-300">{note}</p> : null}
      <label htmlFor={emailId} className="text-[12px] font-bold text-nva-muted dark:text-stone-300">
        {t('home.quick.doorEmail')}
      </label>
      <input
        ref={inputRef}
        id={emailId}
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        value={door.email}
        onChange={(e) => door.setEmail(e.target.value)}
        placeholder={t('home.quick.doorEmailPlaceholder')}
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? errorId : undefined}
        className={cn(
          'min-h-[46px] w-full rounded-xl border border-nva-foot bg-nva-card px-3 text-[16px] text-nva-espresso placeholder:text-nva-muted/70',
          'dark:border-stone-600 dark:bg-stone-900 dark:text-stone-100 dark:placeholder:text-stone-500',
          FOCUS_RING,
        )}
      />
      {message ? (
        <p id={errorId} role="alert" className="m-0 text-[13px] font-semibold leading-snug text-nva-due-ink dark:text-nva-due-ink-dark">
          {message}
        </p>
      ) : null}
      <InkButton type="submit" full disabled={door.loading}>
        {door.loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        {t('home.quick.doorVerify')}
      </InkButton>
    </form>
  );
};

export default DoorCodeReveal;
